/// <reference types="vite/client" />

import i18nLibrary from 'i18next';
import ReactPostprocessor from 'i18next-react-postprocessor';
import HttpApi from 'i18next-http-backend';

import { initReactI18next } from 'react-i18next';
import { getNodeEnv } from '@shared/utils/getEnvironmentVariables';
import globalWindow from '@shared/core/globals';
import { getInitOptions } from './utils';
import { manifest } from '@locales/generated';

const { isDevelopment } = getNodeEnv();

const getHost = () => {
  if (isDevelopment) {
    return process.env.CLIENT_URI ? process.env.CLIENT_URI : `http://${process.env.HOST}:${process.env.PORT}`;
  }
  return '';
};

const RESOURCE_HOST = getHost();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const typedManifest = manifest as Record<string, any>;

/**
 * Resolve a manifest path for the given language + namespace.
 *
 * After PRNT-4468, the manifest is keyed by explicit BCP 47 codes
 * (`en-US`, `de-DE`, …) and supportedLngs is explicit-only
 * (`nonExplicitSupportedLngs: false`), so the resolved chain normally
 * contains only those exact codes. The base-language fallback below
 * (`en-US` → `en`, `de-AT` → `de`) is defensive — it keeps lookups
 * correct if a bare base code reaches this function via a legacy caller,
 * test fixture, or hand-crafted call site. Returns `undefined` when no
 * entry matches; callers (`loadPath`) fall back to an unhashed CDN path.
 */
const resolveManifestPath = (language: string, namespace: string): string | undefined => {
  const exact = typedManifest[language]?.[namespace];
  if (exact) {
    return exact as string;
  }

  // Strip region to try base language: 'en-US' → 'en', 'de-AT' → 'de'
  const baseLanguage = language.split('-')[0];
  if (baseLanguage !== language) {
    return typedManifest[baseLanguage]?.[namespace] as string | undefined;
  }

  return undefined;
};

const backendInitOptions: HttpApi['options'] = {
  // `allowMultiLoading` is false and so `languages` and `namespaces` arguments are arrays of length 1.
  loadPath: (languages, [namespace]) => {
    const [language] = languages;
    if (__STORYBOOK__) {
      return `locales/${language}/${namespace}.json`;
    }
    const targetPath = resolveManifestPath(language, namespace);
    const fallbackPath = `/bliss-apps/joy-web/static/locales/${language}/${namespace}.json`;
    return `${RESOURCE_HOST}${targetPath || fallbackPath}`;
  },
};

if (__BROWSER__) {
  if (__VITE__) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: import.meta requires es2020+ module
    // tsconfig.backend.json TSC program will throw an error unless directed otherwise
    const translationModules = (() => ({}));
    i18nLibrary.use({
      type: 'backend',
      init: () => {},
      // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
      read: async (language: string, namespace: string, callback: Function) => {
        const loader = translationModules[`../../../locales/${language}/${namespace}.json`];
        const module = (await loader()) as { default: Record<string, string> };
        callback(null, module.default);
      },
    });
  } else {
    // In browser, when Webpack, use http backend to load translations and browser lng detector
    i18nLibrary.use(HttpApi);
  }
  i18nLibrary
    // pass the i18n instance to react-i18next.
    .use(initReactI18next) // React post processor for inline react elements in translation
    .use(
      new ReactPostprocessor({
        prefix: '<',
        suffix: '/>',
      }),
    );

  if (!i18nLibrary.isInitialized) {
    i18nLibrary.init({
      ...getInitOptions({
        debug: false, // Too many warning messages crashes the console.
        backend: backendInitOptions,
      }),
      // Seed from the SSR language so async i18next init does not later revert `useSSR()`'s non-English value back to fallback `en-US`.
      lng: globalWindow.__INITIAL_I18N_LANG__ || 'en-US',
    });
  }
}

export default i18nLibrary;
